import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/lib/locale/zh_CN';
import App from './App';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import BannerListPage from './pages/BannerList';
import ProductListPage from './pages/ProductList';
import OrderListPage from './pages/OrderList';
import UserListPage from './pages/UserList';
import CategoryListPage from './pages/CategoryList';
import SubcategoryListPage from './pages/SubcategoryList';
import DiscountListPage from './pages/DiscountList';
import StatsDashboardPage from './pages/StatsDashboard';
import RefundStatisticsPage from './pages/RefundStatistics';
import SalesProfitReportPage from './pages/SalesProfitReport';
import StockAlertPage from './pages/StockAlertPage';
import AuditLogPage from './pages/AuditLogPage';
import ShippingRulePage from './pages/ShippingRulePage';
import HomeSectionsPage from './pages/HomeSections';
import SettingsPageWrap from './pages/SettingsPage';
import RechargeManagementPage from './pages/RechargeManagement';
import MemberManagementPage from './pages/MemberManagement';
import DistributionManagement from './pages/DistributionManagement';
import DistributionStatsPage from './pages/DistributionStats';
import DistributionCommission from './pages/DistributionCommission';
import DistributionConfig from './pages/DistributionConfig';
import ReturnRequestListPage from './pages/ReturnRequestList';
import Forbidden from './pages/Forbidden';
import AuthService from './utils/auth';
import { RequirePermission } from './components/common/Permission';

// 简化的认证守卫组件
const RequireAuth = () => {
  // 简单检查认证状态
  if (!AuthService.isAuthenticated()) {
    console.log('未认证，重定向到登录页');
    return <Navigate to='/login' replace />;
  }

  // 已认证，渲染子组件
  return <Outlet />;
};

// 登录页守卫组件
const LoginGuard = () => {
  // 如果已认证，重定向到首页
  if (AuthService.isAuthenticated()) {
    console.log('已认证，重定向到首页');
    return <Navigate to='/' replace />;
  }

  // 未认证，显示登录页
  return <Outlet />;
};

const AppRouter = () => {
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        token: {
          colorPrimary: '#176B4D',
          colorSuccess: '#23855B',
          colorWarning: '#C68A2B',
          colorError: '#C4473D',
          colorInfo: '#3976B8',
          colorText: '#17221E',
          colorTextSecondary: '#66736D',
          colorBorder: '#E4EAE7',
          colorBgLayout: '#F5F7F5',
          colorBgContainer: '#FFFFFF',
          borderRadius: 8,
          borderRadiusLG: 12,
          controlHeight: 38,
          fontFamily: 'Inter, "PingFang SC", "Microsoft YaHei", system-ui, sans-serif'
        },
        components: {
          Button: { primaryShadow: 'none' },
          Card: { headerBg: '#FFFFFF' },
          Table: { headerBg: '#F7F9F7', headerColor: '#66736D', rowHoverBg: '#F5FAF7' },
          Modal: { titleFontSize: 18 },
          Menu: { darkItemBg: '#102C23', darkSubMenuItemBg: '#0C241C', darkItemSelectedBg: '#176B4D' }
        }
      }}
    >
      <BrowserRouter>
        <Routes>
          {/* 登录路由 */}
          <Route element={<LoginGuard />}>
            <Route path='/login' element={<Login />} />
          </Route>

          {/* 需要认证的路由 */}
          <Route element={<RequireAuth />}>
            <Route path='/403' element={<Forbidden />} />
            <Route path='/' element={<App />}>
              <Route index element={<Dashboard />} />
              <Route path='banner' element={<BannerListPage />} />
              <Route path='product' element={<ProductListPage />} />
              <Route path='order' element={<OrderListPage />} />
              <Route path='user' element={<UserListPage />} />
              <Route path='category' element={<CategoryListPage />} />
              <Route path='subcategory' element={<SubcategoryListPage />} />
              <Route path='discount' element={<DiscountListPage />} />
              <Route path='stats' element={<StatsDashboardPage />} />
              <Route path='refund-stats' element={<RefundStatisticsPage />} />
              <Route path='sales-report' element={<SalesProfitReportPage />} />
              <Route path='stock-alert' element={<StockAlertPage />} />
              <Route
                path='settings'
                element={(
                  <RequirePermission anyOf={['settings:view', 'system:settings:view', 'settings:manage']}>
                    <SettingsPageWrap />
                  </RequirePermission>
                )}
              />
              <Route path='recharge' element={<RechargeManagementPage />} />
              <Route path='member' element={<MemberManagementPage />} />
              <Route path='user/recharge' element={<RechargeManagementPage />} />
              <Route path='user/member' element={<MemberManagementPage />} />
              <Route path='distribution' element={<DistributionManagement />} />
              <Route path='distribution-stats' element={<DistributionStatsPage />} />
              <Route path='distribution-commission' element={<DistributionCommission />} />
              <Route path='distribution-config' element={<DistributionConfig />} />
              <Route path='return-request' element={<ReturnRequestListPage />} />
              <Route path='audit-log' element={<AuditLogPage />} />
              <Route path='shipping-rule' element={<ShippingRulePage />} />
              <Route
                path='home-sections'
                element={(
                  <RequirePermission anyOf={['settings:view', 'system:settings:view', 'settings:manage']}>
                    <HomeSectionsPage />
                  </RequirePermission>
                )}
              />
            </Route>
          </Route>

          {/* 404重定向 */}
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default AppRouter;